// Conversations: every conversation this device session can list, runs' and interactive
// workspace sessions'. Metadata only; the content opens on the conversation page.
import { ChatsCircle, LockKey } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import type { Conversation } from "../api/client";
import { conversationsQuery } from "../api/queries";
import { conversationKind } from "../conversation/display";
import { useDevice } from "../device/context";
import { type Messages, useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { E2EEBadge, Mono, Pill } from "../ui/status";
import { dayTime } from "../workloads/format";
import { shortId } from "./format";
import { sectionCrumbs } from "../layout/nav";

const columns = (t: Messages["conversation"]): Column<Conversation>[] => [
  {
    key: "id",
    header: t.columns.conversation,
    cell: (c) => (
      // A task group's chat conversation has the group's id (D82): open it as the chat.
      <Link to={c.kind === "group" ? `/task-groups/${c.id}/chat` : `/conversations/${c.id}`} className="font-mono text-[12.5px] text-primary" title={c.id}>
        {shortId(c.id)}
      </Link>
    ),
  },
  { key: "kind", header: t.columns.kind, cell: (c) => <Pill tone="primary">{conversationKind(c.kind)}</Pill> },
  {
    key: "about",
    header: t.columns.about,
    cell: (c) =>
      c.run_id ? (
        <Link to={`/runs/${c.run_id}`} className="font-mono text-[12.5px]">
          {t.aboutRun(shortId(c.run_id))}
        </Link>
      ) : (
        <Mono>{c.workspace_id ? t.aboutWorkspace(shortId(c.workspace_id)) : "—"}</Mono>
      ),
    hideBelow: "sm",
  },
  { key: "created", header: t.columns.created, cell: (c) => dayTime(c.created_at), hideBelow: "lg" },
  { key: "last", header: t.columns.lastEvent, cell: (c) => (c.last_event_at == null ? "—" : dayTime(c.last_event_at)), hideBelow: "md" },
];

export default function ConversationsPage() {
  const { conversation: t } = useT();
  const device = useDevice();
  const linked = device.kind === "linked";
  const query = useQuery(conversationsQuery(linked));
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {t.title} <E2EEBadge />
          </span>
        }
        breadcrumb={sectionCrumbs("conversations")}
        description={t.description}
      />
      {!linked ? <LinkFirst /> : null}
      {linked && query.isPending ? <Loading /> : null}
      {linked && query.isError ? <ErrorState message={query.error.message} /> : null}
      {query.data?.length === 0 ? (
        <EmptyState icon={<ChatsCircle weight="duotone" />} title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      {linked && query.data && query.data.length > 0 ? (
        <div className={cn(cardClass, "p-3 lg:p-4")}>
          <DataTable label={t.title} columns={columns(t)} rows={query.data} rowKey={(c) => c.id} />
        </div>
      ) : null}
    </>
  );
}

/** Conversation endpoints need a device session; without one there is nothing to list. */
export function LinkFirst() {
  const t = useT().conversation;
  return (
    <div className={cn(cardClass, "flex items-start gap-3 p-5")} role="status">
      <LockKey weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
      <p className="m-0 text-[13.5px] text-gray-700">{t.linkFirst(<Link to="/devices">{t.linkThisBrowser}</Link>)}</p>
    </div>
  );
}
